import type { Notification } from '../types';
import * as notifyApi from '../api/notify';
import { SingletonAsyncCache } from './asyncCache';

const TYPE_FROM_BACKEND: Record<notifyApi.BackendNotificationType, Notification['type']> = {
  DISBURSE_SUCCESS: 'disburse_success',
  DISBURSE_FAILED: 'disburse_failed',
  LOAN_APPROVED: 'loan_approved',
  LOAN_REJECTED: 'loan_rejected',
  LOAN_NEED_MORE_INFO: 'general',
  DEBT_DUE: 'debt_due',
  DEBT_OVERDUE: 'debt_overdue',
  DEBT_REMINDER: 'debt_reminder',
  HARVEST_UPDATE: 'general',
  GENERAL: 'general',
};

function toFrontendNotification(n: notifyApi.BackendNotification): Notification {
  return {
    id: n._id,
    farmerId: n.farmerId ?? '',
    title: n.title ?? n.templateCode,
    content: n.renderedBody,
    type: TYPE_FROM_BACKEND[n.notificationType] ?? 'general',
    ngayTao: n.createdAt,
    daDoc: n.isRead,
  };
}

/** `GET /notifications/me` is always scoped to the calling JWT, not an
 * arbitrary farmerId — there's exactly one real list per logged-in user
 * (farmer or HTX staff alike), hence a Singleton rather than a keyed cache. */
const myNotifications = new SingletonAsyncCache<Notification[]>(
  async () => (await notifyApi.listMyNotifications()).map(toFrontendNotification),
  [],
  'notifications:mine',
);

export function getNotifications(): Notification[] {
  return myNotifications.get();
}

/** `farmerId` is accepted only for call-site compatibility with the old mock
 * API — every caller is already asking about their own logged-in identity,
 * which is exactly what the real endpoint returns. */
export function getNotificationsByFarmer(_farmerId: string): Notification[] {
  return myNotifications.get();
}

export function getUnreadNotificationsByFarmer(_farmerId: string): Notification[] {
  return myNotifications.get().filter((n) => !n.daDoc);
}

function refresh() {
  myNotifications.invalidate();
  myNotifications.get();
}

export async function markNotificationRead(id: string): Promise<Notification> {
  const updated = await notifyApi.markNotificationRead(id);
  refresh();
  return toFrontendNotification(updated);
}

export async function markAllNotificationsRead(): Promise<void> {
  await notifyApi.markAllNotificationsRead();
  refresh();
}
